import { useEffect, useState } from "react";
import CookieConsent from "react-cookie-consent";
import { initGA } from "@/lib/analytics";
import { useLanguage } from "@/lib/language";
import { Cookie, Shield } from "lucide-react";

export function CookieConsentBanner() {
  const { t, language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  const isRTL = language === "he";

  useEffect(() => {
    // Small delay to ensure proper rendering
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const handleAccept = () => {
    // Initialize Google Analytics when user accepts cookies
    initGA();
    console.log("Cookies accepted - Google Analytics initialized");
  };

  const handleDecline = () => {
    console.log("Cookies declined");
    // Don't initialize GA if user declines
  };

  if (!isVisible) return null;

  return (
    <CookieConsent
      location="bottom"
      buttonText={t("cookieConsentAccept")}
      declineButtonText={t("cookieConsentDecline")}
      enableDeclineButton
      onAccept={handleAccept}
      onDecline={handleDecline}
      cookieName="samantha-azoulay-cookie-consent"
      style={{
        background:
          "linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        padding: "0",
        alignItems: "stretch",
        boxShadow:
          "0 -8px 32px -4px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
        border: "none",
        borderTop: "1px solid rgba(255, 255, 255, 0.1)",
        direction: isRTL ? "rtl" : "ltr",
      }}
      buttonStyle={{
        background:
          "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.8) 100%)",
        color: "hsl(var(--primary-foreground))",
        fontSize: "15px",
        padding: "12px 32px",
        borderRadius: "12px",
        fontWeight: "600",
        cursor: "pointer",
        border: "none",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow:
          "0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)",
        margin: isRTL ? "0 0 0 12px" : "0 12px 0 0",
      }}
      declineButtonStyle={{
        background: "rgba(255, 255, 255, 0.08)",
        color: "rgba(255, 255, 255, 0.9)",
        fontSize: "15px",
        padding: "12px 32px",
        borderRadius: "12px",
        fontWeight: "600",
        cursor: "pointer",
        border: "1px solid rgba(255, 255, 255, 0.15)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        backdropFilter: "blur(10px)",
      }}
      contentStyle={{
        flex: "1 1 auto",
        margin: "0",
        padding: "0",
      }}
      containerClasses="cookie-consent-container"
      buttonWrapperClasses="cookie-consent-buttons"
      expires={365}
      overlay
      overlayStyle={{
        background: "rgba(0, 0, 0, 0.4)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "24px 32px",
          display: "flex",
          alignItems: "center",
          gap: "24px",
          flexDirection: isRTL ? "row-reverse" : "row",
          flexWrap: "wrap",
        }}
      >
        {/* Icon */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "56px",
            height: "56px",
            borderRadius: "16px",
            background:
              "linear-gradient(135deg, hsl(var(--primary) / 0.2) 0%, hsl(var(--primary) / 0.1) 100%)",
            border: "1px solid hsl(var(--primary) / 0.3)",
            flexShrink: 0,
          }}
        >
          <Cookie size={28} style={{ color: "hsl(var(--primary))" }} />
        </div>

        {/* Content */}
        <div
          style={{
            flex: "1 1 300px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row-reverse" : "row",
              justifyContent: isRTL ? "flex-end" : "flex-start",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "rgba(255, 255, 255, 0.95)",
                margin: "0",
                letterSpacing: "-0.01em",
              }}
            >
              {t("cookieConsentTitle")}
            </h3>
            <Shield
              size={16}
              style={{ color: "hsl(var(--primary))", flexShrink: 0 }}
            />
          </div>

          <p
            style={{
              fontSize: "14px",
              lineHeight: "1.6",
              color: "rgba(255, 255, 255, 0.7)",
              margin: "0",
              textAlign: isRTL ? "right" : "left",
              direction: isRTL ? "rtl" : "ltr",
            }}
          >
            {t("cookieConsentMessage")}{" "}
            <a
              href={`/${language}/privacy-policy`}
              style={{
                color: "hsl(var(--primary))",
                textDecoration: "none",
                fontWeight: "600",
                borderBottom: "1px solid hsl(var(--primary) / 0.3)",
                transition: "all 0.2s",
                paddingBottom: "1px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderBottomColor = "hsl(var(--primary))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderBottomColor =
                  "hsl(var(--primary) / 0.3)";
              }}
            >
              {t("cookieConsentLearnMore")}
            </a>
          </p>
        </div>
      </div>
    </CookieConsent>
  );
}
